import { Box, styled } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import type { Control, FieldValues } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import type { ReservationFormInput } from '../schema/reservationForm.schema';
import {
  type TripArrayFieldKey,
  useReservationFormMachines,
} from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../useFieldActorSnapshot';

function mergeDatePart(
  base: DateTime | null,
  newDate: DateTime | null,
): DateTime | null {
  if (newDate === null) {
    return null;
  }
  if (!newDate.isValid) {
    return base;
  }
  if (!base?.isValid) {
    return newDate.startOf('day');
  }
  return base.set({
    year: newDate.year,
    month: newDate.month,
    day: newDate.day,
  });
}

function mergeTimePart(
  base: DateTime | null,
  newTime: DateTime | null,
): DateTime | null {
  if (newTime === null) {
    return null;
  }
  if (!newTime.isValid) {
    return base;
  }
  if (!base?.isValid) {
    return newTime;
  }
  return base.set({
    hour: newTime.hour,
    minute: newTime.minute,
    second: newTime.second,
  });
}

const PREFIX = 'TripArrayFieldDateTimeInput';

const classes = {
  container: `${PREFIX}-container`,
  appointment: `${PREFIX}-appointment`,
  pickup: `${PREFIX}-pickup`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    '& > *': { flex: '1 1 140px', minWidth: 0 },
    [`&.${classes.pickup}`]: {
      marginLeft: theme.spacing(4),
      marginTop: theme.spacing(-1),
      marginBottom: theme.spacing(1),
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: '75%',
    },
  },
}));

export function TripArrayFieldDateTimeInput({
  tripField,
  index,
  required,
}: {
  /** Luxon datetime; `appointmentDate` / `pickupDate` are separate string fields synced from this control. */
  tripField: Extract<TripArrayFieldKey, 'appointmentDateTime' | 'pickupDateTime'>;
  index: number;
  required?: boolean;
}) {
  const { control, setValue } = useFormContext<ReservationFormInput>();
  const { t } = useTranslation('common');
  const { fieldRefs } = useReservationFormMachines();
  const actor = fieldRefs[tripField];
  useFieldActorSnapshot(actor);

  // const timeFieldLabel = 
  //   pickupTimeZoneName
  //     ? `${t('member.time')} (${pickupTimeZoneName})`
  //     : t('member.time');

  const requiredFieldMessage = t('common.requiredField');
  const invalidDateMessage = t('common.invalidDate');

  const name = `trips.${index}.${tripField}` as const;

  const sendChange = (next: DateTime | null) => {
    actor.send({
      type: 'CHANGE',
      index,
      value: next?.toISO() ?? '',
    });
  };

  const syncPairedDateString = (next: DateTime | null) => {
    const dateField =
      tripField === 'pickupDateTime' ? 'pickupDate' : 'appointmentDate';
    setValue(`trips.${index}.${dateField}`, next?.toISODate() ?? '', {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <Controller
      name={name}
      control={control as unknown as Control<FieldValues>}
      rules={{
        required: required && requiredFieldMessage,
        validate: (value: DateTime | null) => {
          if (required && !value?.isValid) {
            return invalidDateMessage;
          }
          if (!required && value && !value.isValid) {
            return invalidDateMessage;
          }
          return true;
        },
      }}
      render={({ field }) => (
        <StyledBox className={`${classes.container} ${tripField === 'appointmentDateTime' ? classes.appointment : classes.pickup}`}>
          <DatePicker
            label={t('member.date')}
            value={field.value ?? null}
            slotProps={{
              textField: {
                variant: 'outlined',
                required,
                onFocus: () => actor.send({ type: 'FOCUS', index }),
                sx: {
                  width: '100%',
                  '& .MuiInputAdornment-root .MuiIconButton-root': {
                    padding: '4px',
                    marginLeft: '-15px',
                  },
                },
              },
            }}
            onChange={(date) => {
              const next = mergeDatePart(field.value ?? null, date);
              field.onChange(next);
              syncPairedDateString(next);
              sendChange(next);
            }}
          />
          <TimePicker
            label={t('member.time')}
            value={field.value ?? null}
            closeOnSelect={false}
            slotProps={{
              textField: {
                variant: 'outlined',
                required,
                onFocus: () => actor.send({ type: 'FOCUS', index }),
                sx: { width: '100%' },
              },
            }}
            onChange={(time) => {
              const next = mergeTimePart(field.value ?? null, time);
              field.onChange(next);
              syncPairedDateString(next);
              sendChange(next);
              if (time) {
                field.onBlur();
              }
            }}
          />
        </StyledBox>
      )}
    />
  );
}
